/**
 * PATH: packages/ui/src/kanban/components/useKanbanCardPresentation.ts
 * FILE_ID: UI-KANBAN-CARD-PRESENTATION-001
 * PURPOSE: Derive stable member and email presentation facts for one card tile.
 * WHY: Keep the tile frame focused on interaction/layout composition under the file ceiling.
 * FLOW: card + optional resolvers → memoized members/email metadata.
 * DEPENDENCIES: KanbanCard props and API presentation types.
 * RELATED: KanbanCard.tsx; KanbanCardEmailMeta.tsx; KanbanCardFooter.tsx.
 */
import { useMemo } from 'react'

import type { KanbanEmailCardMeta, KanbanMember } from '../api'
import type { KanbanCardProps } from './KanbanCard.types'

export function useKanbanCardPresentation(
  card: KanbanCardProps['card'],
  resolveMember: KanbanCardProps['resolveMember'],
  resolveEmailMeta: KanbanCardProps['resolveEmailMeta'],
) {
  const assignedMembers = (card.assigned_user_ids ?? [])
    .map((id) => resolveMember?.(id))
    .filter((member): member is KanbanMember => member !== undefined)

  const emailMeta = useMemo<KanbanEmailCardMeta | undefined>(() => {
    if (!card.primary_email) return undefined
    if (
      card.primary_email_from_name != null ||
      card.primary_email_from_address != null ||
      card.primary_email_received_at != null
    ) {
      return {
        from_name: card.primary_email_from_name ?? undefined,
        from_address: card.primary_email_from_address ?? undefined,
        received_at: card.primary_email_received_at ?? undefined,
      }
    }
    return resolveEmailMeta?.(card.primary_email)
  }, [
    card.primary_email,
    card.primary_email_from_name,
    card.primary_email_from_address,
    card.primary_email_received_at,
    resolveEmailMeta,
  ])

  return { assignedMembers, emailMeta }
}
